import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/Card';
import { Eye, Download, MoreVertical, AlertCircle, Filter, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface StatCardProps {
  title: string;
  subtitle?: string;
  count?: number;
  actionLabel?: string;
  actionLink?: string;
  onQuickView?: () => void;
  onSolveIssues?: () => void;
  children: React.ReactNode;
  moreMenuItems?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }>;
  isFiltered?: boolean;
  filterNote?: string;
}
export function StatCard({
  title,
  subtitle,
  count,
  actionLabel,
  actionLink,
  onQuickView,
  onSolveIssues,
  children,
  moreMenuItems,
  isFiltered,
  filterNote
}: StatCardProps) {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    }
    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMoreMenuOpen]);
  return <Card className="flex flex-col h-full" data-id="element-3965" data-mp="jycvfgxrs2xe">
      {/* Header */}
      <div className="flex items-start justify-between mb-4" data-id="element-3966" data-mp="17mme49zosakb">
        <div className="flex-1" data-id="element-3967" data-mp="1clpc5ah91gis">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-3968" data-mp="1cknz50goajyf">
            <h3 className="text-base font-bold text-gray-900" data-id="element-3969" data-mp="xwt0z4jwbndy">{title}</h3>
            {count !== undefined && <span className="text-2xl font-bold text-gray-900" data-id="element-3970" data-mp="12uchkb42gc92">{count}</span>}
            {isFiltered && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ml-1" data-id="element-3971" data-mp="1qjdxejf8apod">
                <Filter size={10} data-id="element-3972" data-mp="1v7ogk7bwkjcl" />
                Filtered by site
              </motion.div>}
            {filterNote && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex items-center gap-1 bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-1" data-id="element-3973" data-mp="1m8jit3860u1h">
                <Info size={10} data-id="element-3974" data-mp="13ibs5klnjw26" />
                {filterNote}
              </motion.div>}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5" data-id="element-3975" data-mp="ykjqvzz4axlb">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-2" data-id="element-3976" data-mp="3a5ixu7nfvp2a">
          {onQuickView && <button onClick={onQuickView} className="text-gray-400 hover:text-gray-600 transition-colors p-1" title="Quick view" data-id="element-3977" data-mp="3f55i311oq5kl">
              <Eye size={18} data-id="element-3978" data-mp="3o63dww1yz6s3" />
            </button>}

          {onSolveIssues && <button onClick={onSolveIssues} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors" data-id="element-3979" data-mp="2rgckz19btroe">
              <AlertCircle size={16} data-id="element-3980" data-mp="30gixokx7anmi" />
              Solve!
            </button>}

          {moreMenuItems && moreMenuItems.length > 0 && <div className="relative" ref={moreMenuRef} data-id="element-3981" data-mp="359c0h393zlvy">
              <button onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)} className="text-gray-400 hover:text-gray-600 transition-colors p-1" title="More options" data-id="element-3982" data-mp="27rbx7ge6rnym">
                <MoreVertical size={18} data-id="element-3983" data-mp="j8pnjudnd3dj" />
              </button>

              <AnimatePresence data-id="element-3984" data-mp="eafmx495lakj">
                {isMoreMenuOpen && <motion.div initial={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50" data-id="element-3985" data-mp="s0j77poh2u7c">
                    {moreMenuItems.map((item, index) => <button key={index} onClick={() => {
                item.onClick();
                setIsMoreMenuOpen(false);
              }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3986" data-mp="2ylgifno1eigj" data-mpi={index}>
                        {item.icon && <span className="text-gray-400" data-id="element-3987" data-mp="2ewfvg50q0irt">{item.icon}</span>}
                        <span data-id="element-3988" data-mp="2jxn220r4nbww">{item.label}</span>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1" data-id="element-3989" data-mp="45g1zur4gzk4">{children}</div>

      {/* Footer Action */}
      {actionLabel && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-3990" data-mp="94ivchxqphc9">
          {actionLink ? <Link to={actionLink} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3991" data-mp="926gidna3n3f">
              {actionLabel} →
            </Link> : <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3992" data-mp="3tu210grvep7w">
              {actionLabel} →
            </button>}
        </div>}
    </Card>;
}